import React from 'react';
import PropTypes from 'prop-types';
import { replaceStrValues } from 'platform/shared/health-apps';
import { arrayBuilderPages } from '~/platform/forms-system/src/js/patterns/array-builder';
import {
  titleUI,
  radioUI,
  textUI,
  radioSchema,
  currencyUI,
  currencySchema,
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderYesNoUI,
  arrayBuilderYesNoSchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import {
  typeOfIncomeLabels,
  incomeRecipientTypeLabels,
} from '../../../../utils/labels';
import { customTextSchema } from '../../../definitions';
import { GrossMonthlyIncomeAlert } from '../../../../components/FormAlerts';
import {
  isRelationshipToVeteran,
  shouldSkipIncomePages,
} from '../../../../utils/helpers';
import content from '../../../../utils/content.json';

const allRecipientTypes = {
  ...incomeRecipientTypeLabels,
  CUSTODIAN: 'Custodian',
  CUSTODIAN_SPOUSE: "Custodian's spouse",
};

const filteredLabel = is2025Enabled =>
  is2025Enabled
    ? [...Object.keys(allRecipientTypes)]
    : [...Object.keys(incomeRecipientTypeLabels)];

const GrossDescription = ({ formData }) => {
  const relationShip = isRelationshipToVeteran(formData);
  let descriptionContent =
    'Next we’ll ask you the gross monthly income you and your dependents receive. You can add up to 4 income sources.';
  if (relationShip === 'child') {
    descriptionContent =
      'Next we’ll ask you the gross monthly income your household receives. You can add up to 4 income sources.';
  } else if (relationShip === 'custodian') {
    descriptionContent =
      'Next we’ll ask you the gross monthly income the household receives. You can add up to 4 income sources.';
  }
  const shouldShowAlert =
    formData?.landMarketable === true ||
    formData?.transferredAssets === true ||
    formData?.moreThanFourIncomeSources === true ||
    // This is for the 2025 version response.
    formData?.incomeSourcesCount === 'MORE_THAN_FIVE_SOURCES' ||
    formData?.totalNetWorth === true ||
    formData?.otherIncome === true;
  return (
    <div>
      {shouldShowAlert && <GrossMonthlyIncomeAlert />}
      <p>{descriptionContent}</p>
    </div>
  );
};

GrossDescription.propTypes = {
  formData: PropTypes.shape({
    landMarketable: PropTypes.bool,
    transferredAssets: PropTypes.bool,
    moreThanFourIncomeSources: PropTypes.bool,
    incomeSourcesCount: PropTypes.string,
    totalNetWorth: PropTypes.bool,
    otherIncome: PropTypes.bool,
  }),
};

/** Array builder options for income sources */
export const options = {
  arrayPath: 'incomeEntries',
  nounSingular: content['financial-info-monthly-income-noun-singular'],
  nounPlural: content['financial-info-monthly-income-noun-plural'],
  maxItems: 4,
  required: false,
  text: {
    cancelAddTitle: replaceStrValues(
      content['array-cancel-add-title'],
      `${content['financial-info-monthly-income-noun-singular']}?`,
    ),
    cancelEditTitle: replaceStrValues(
      content['array-cancel-edit-title'],
      `${content['financial-info-monthly-income-noun-singular']}?`,
    ),
    cancelAddDescription:
      content['financial-info-monthly-income-cancel-add-description'],
    cancelAddReviewDescription:
      content['financial-info-monthly-income-cancel-add-description'],
    cancelEditDescription:
      content['financial-info-monthly-income-cancel-edit-description'],
    cancelEditReviewDescription:
      content['financial-info-monthly-income-cancel-edit-description'],
    cancelAddYes: content['array-cancel-add-yes'],
    cancelAddNo: content['array-cancel-add-no'],
    cancelEditYes: content['array-cancel-edit-yes'],
    cancelEditNo: content['array-cancel-edit-no'],
    deleteDescription: replaceStrValues(content['array-delete-description'], [
      `${content['financial-info-monthly-income-noun-plural']}.`,
      `${content['financial-info-monthly-income-noun-singular']}.`,
    ]),
    deleteNo: content['array-delete-no'],
    deleteTitle: replaceStrValues(
      content['array-delete-title'],
      `${content['financial-info-monthly-income-noun-singular']}?`,
    ),
    deleteYes: content['array-delete-yes'],
    alertMaxItems: (
      <div>
        <p className="vads-u-margin-top--0">
          You have added the maximum number of allowed monthly income sources
          for this application. Additional income sources can be added using an
          Income and Asset Statement in Support of Claim for Pension or Parents’
          Dependency and Indemnity Compensation (VA Form 21P-0969).
        </p>
        <p>
          You can submit this form online, upload it at the end of this
          application, or send it to us by mail.
        </p>
        <span className="vads-u-display--block vads-u-margin-bottom--2">
          <va-link
            href="https://www.va.gov/supporting-forms-for-claims/submit-income-and-asset-statement-form-21p-0969"
            external
            text="Submit income and asset statement to support your claim"
          />
        </span>
        <span className="vads-u-display--block">
          <va-link
            href="https://www.va.gov/find-forms/about-form-21p-0969"
            external
            text="Get VA Form 21P-0969 to download"
          />
        </span>
      </div>
    ),
    // headline for each card: use the selected type of income label
    getItemName: item =>
      (item && item.incomeType && typeOfIncomeLabels[item.incomeType]) ||
      content['financial-info-monthly-income-default-item-name'],
    // description shown under the card title: show monthly amount if present
    cardDescription: item =>
      item?.monthlyIncome
        ? `$${item.monthlyIncome}`
        : content['financial-info-monthly-income-default-item-description'],
    // summary page heading
    summaryTitle: content['financial-info-monthly-income-summary-title'],
    // yes/no question text on summary page
    yesNoBlankReviewQuestion:
      content['financial-info-monthly-income-blank-review-question'],
  },
};

/** @returns {PageSchema} */
const introPage = {
  uiSchema: {
    ...titleUI(
      content['financial-info-monthly-income-intro-title'],
      GrossDescription,
    ),
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

/** @returns {PageSchema} */
const incomeSummaryPage = {
  uiSchema: {
    ...titleUI(content['financial-info-monthly-income-summary-page-title']),
    'view:hasMonthlyIncomeSource': arrayBuilderYesNoUI(
      options,
      {
        title: content['financial-info-monthly-income-summary-add'],
        hint: content['array-empty-hint'],
        errorMessages: {
          required:
            content['financial-info-monthly-income-summary-add-error-req'],
        },
      },
      {
        title: content['financial-info-monthly-income-summary-add-another'],
        hint: content['array-empty-hint'],
        errorMessages: {
          required:
            content[
              'financial-info-monthly-income-summary-add-another-error-req'
            ],
        },
      },
    ),
  },
  schema: {
    type: 'object',
    required: ['view:hasMonthlyIncomeSource'],
    properties: {
      'view:hasMonthlyIncomeSource': arrayBuilderYesNoSchema,
    },
  },
};

/** @returns {PageSchema} */
const monthlyDetailsPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['financial-info-monthly-income-details-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    recipient: {
      ...radioUI({
        title: content['financial-info-monthly-income-details-recipient'],
      }),
      'ui:options': {
        updateSchema: (
          _itemData,
          schema,
          _uiSchema,
          _index,
          _path,
          fullData,
        ) => {
          return {
            ...schema,
            enum: filteredLabel(
              fullData?.survivorsBenefitsForm2025VersionEnabled,
            ),
            enumNames: filteredLabel(
              fullData?.survivorsBenefitsForm2025VersionEnabled,
            ).map(key => allRecipientTypes[key]),
          };
        },
      },
    },
    recipientName: textUI({
      title: content['financial-info-monthly-income-details-recipient-name'],
      expandUnder: 'recipient',
      expandUnderCondition: field => field === 'CHILD',
      required: (formData, index, fullData) => {
        const items = formData?.incomeEntries ?? fullData?.incomeEntries;
        const item = items?.[index];
        return item?.recipient === 'CHILD';
      },
    }),
    incomeType: radioUI({
      title: content['financial-info-monthly-income-details-income-type'],
      labels: typeOfIncomeLabels,
    }),
    otherIncomeType: textUI({
      title: content['financial-info-monthly-income-details-other-income-type'],
      expandUnder: 'incomeType',
      expandUnderCondition: field => field === 'OTHER',
      required: (formData, index, fullData) => {
        const items = formData?.incomeEntries ?? fullData?.incomeEntries;
        const item = items?.[index];
        return item?.incomeType === 'OTHER';
      },
    }),
    incomePayer: textUI({
      title: content['financial-info-monthly-income-details-income-payer'],
      classNames: 'vads-u-margin-bottom--2',
      hint: content['financial-info-monthly-income-details-income-payer-hint'],
      expandUnder: 'incomeType',
      expandUnderCondition: field => field !== 'SOCIAL_SECURITY' && field,
      required: (formData, index, fullData) => {
        const items = formData?.incomeEntries ?? fullData?.incomeEntries;
        const item = items?.[index];
        return !!item?.incomeType && item?.incomeType !== 'SOCIAL_SECURITY';
      },
    }),
    monthlyIncome: currencyUI({
      title: content['financial-info-monthly-income-details-monthly-income'],
      max: 999999999,
    }),
  },
  schema: {
    type: 'object',
    required: ['recipient', 'incomeType', 'incomePayer', 'monthlyIncome'],
    properties: {
      recipient: radioSchema(Object.keys(allRecipientTypes)),
      recipientName: customTextSchema,
      incomeType: radioSchema(Object.keys(typeOfIncomeLabels)),
      otherIncomeType: customTextSchema,
      incomePayer: customTextSchema,
      monthlyIncome: currencySchema,
    },
  },
};

// Should show when the following are true:
// 2022: If survivors pension is checked.
// 2025: If survivors pension is checked and if incomeSourcesCount is not 'NO_INCOME'.
export const grossMonthlyIncomePages = arrayBuilderPages(
  options,
  pageBuilder => ({
    grossMonthlyIncome: pageBuilder.introPage({
      title: content['financial-info-monthly-income-intro-title'],
      path: 'financial-information/gross-monthly-income',
      depends: formData =>
        formData?.claims?.survivorsPension === true &&
        !shouldSkipIncomePages(formData),
      uiSchema: introPage.uiSchema,
      schema: introPage.schema,
    }),
    addIncomeSource: pageBuilder.summaryPage({
      title: content['financial-info-monthly-income-summary-add'],
      path: 'financial-information/add-income-source',
      depends: formData =>
        formData?.claims?.survivorsPension === true &&
        !shouldSkipIncomePages(formData),
      schema: incomeSummaryPage.schema,
      uiSchema: incomeSummaryPage.uiSchema,
    }),
    monthlyIncomeDetails: pageBuilder.itemPage({
      title: content['financial-info-monthly-income-details-title'],
      path: 'financial-information/:index/monthly-income-details',
      depends: formData =>
        formData?.claims?.survivorsPension === true &&
        !shouldSkipIncomePages(formData),
      schema: monthlyDetailsPage.schema,
      uiSchema: monthlyDetailsPage.uiSchema,
    }),
  }),
);

export default grossMonthlyIncomePages;
